import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBolt,
    faExclamationTriangle,
    faCheckCircle,
    faClipboardCheck,
    faBell,
    faCalendar,
    faPlug,
    faShield,
    faTools,
    faWrench,
    faLightbulb,
    faWarning,
    faPlus,
    faTasks
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import './ElectricalSafetyDashboard.css';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';

// Static data structure matching Roof Safety Dashboard
const stats = [
  {
    key: 'totalInspections',
    label: 'Total Inspections',
    icon: faClipboardCheck,
    total: 45,
    failed: 0,
    status: 'good',
    navTarget: 'equipment-logs',
  },
  {
    key: 'pendingIssues',
    label: 'Pending Issues',
    icon: faExclamationTriangle,
    total: 3,
    failed: 3,
    status: 'alert',
    navTarget: 'circuit-breakers',
  },
  {
    key: 'completedChecks',
    label: 'Completed Checks',
    icon: faCheckCircle,
    total: 42,
    failed: 0,
    status: 'good',
    navTarget: 'pat-testing',
  },
  {
    key: 'upcomingTests',
    label: 'Upcoming Tests',
    icon: faCalendar,
    total: 5,
    failed: 0,
    status: 'warning',
    navTarget: 'rcd-testing',
  },
  {
    key: 'electricalPanels',
    label: 'Electrical Panels',
    icon: faPlug,
    total: 12,
    failed: 1,
    status: 'warning',
    navTarget: 'fuse-boards',
  },
  {
    key: 'safetySystems',
    label: 'Safety Systems',
    icon: faShield,
    total: 8,
    failed: 0,
    status: 'good',
    navTarget: 'emergency-power',
  },
  {
    key: 'maintenanceTasks',
    label: 'Maintenance Tasks',
    icon: faWrench,
    total: 15,
    failed: 2,
    status: 'alert',
    navTarget: 'switchgear',
  },
];

// Safety Risk Overview Card Component
const SafetyRiskCard = () => {
  const riskData = {
    overallRisk: "Medium",
    passedCount: 112,
    dueSoonCount: 9,
    failedCount: 7,
    complianceRate: "92%"
  };

  return (
    <div className="dashboard-panel risk-panel">
      <div className="panel-title">
        <FontAwesomeIcon icon={faExclamationTriangle} /> Safety Risk Overview
      </div>
      
      {/* Visual Chart based on data - moved to top */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '-20px' }}>
        <div style={{ position: 'relative', width: '140px', height: '140px' }}>
          {/* Pie chart */}
          <div style={{
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, #10b981 0deg 310deg, #f59e0b 310deg 330deg, #dc2626 330deg 360deg)',
            border: '2px solid #fff'
          }}>
          </div>
        </div>
      </div>

      {/* Status counts - moved below chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
          <span style={{ color: '#0f172a' }}>Passed:</span>
          <span style={{ color: '#10b981', fontWeight: '600' }}>{riskData.passedCount}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
          <span style={{ color: '#0f172a' }}>Due Soon:</span>
          <span style={{ color: '#f59e0b', fontWeight: '600' }}>{riskData.dueSoonCount}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', cursor: 'pointer' }}>
          <span style={{ color: '#0f172a' }}>Failed:</span>
          <span style={{ color: '#dc2626', fontWeight: '600' }}>{riskData.failedCount}</span>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
          Compliance Rate: {riskData.complianceRate}
        </div>
        <div style={{ marginTop: '4px', fontSize: '10px', color: '#92400e', fontWeight: '500' }}>
          Overall Risk: {riskData.overallRisk}
        </div>
      </div>
    </div>
  );
};

// Recent Activity Card Component
const RecentActivityCard = ({ onViewAllClick }) => {
  const navigate = useNavigate();
  const { id: propertyId } = useParams();
  const recentActivity = [
    {
      message: "EL-14 marked as FAILED",
      timestamp: "26 Oct, 14:32",
      type: "Total Inspections",
      status: "FAILED"
    },
    {
      message: "EL-09 inspected and PASSED",
      timestamp: "26 Oct, 09:10",
      type: "Completed Checks",
      status: "PASSED"
    },
    {
      message: "EL-22 added to Floor 3",
      timestamp: "25 Oct, 16:48",
      type: "Electrical Panels",
      status: "ADDED"
    },
    {
      message: "EL-03 scheduled for maintenance",
      timestamp: "25 Oct, 11:05",
      type: "Maintenance Tasks",
      status: "TASK"
    },
    {
      message: "EL-15 inspection completed",
      timestamp: "25 Oct, 08:30",
      type: "Safety Systems",
      status: "PASSED"
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "FAILED":
        return <FontAwesomeIcon icon={faWarning} style={{ color: '#dc2626', fontSize: '12px' }} />;
      case "PASSED":
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981', fontSize: '12px' }} />;
      case "ADDED":
        return <FontAwesomeIcon icon={faPlus} style={{ color: '#3b82f6', fontSize: '12px' }} />;
      case "TASK":
        return <FontAwesomeIcon icon={faTasks} style={{ color: '#64748b', fontSize: '12px' }} />;
      default:
        return <FontAwesomeIcon icon={faTasks} style={{ color: '#64748b', fontSize: '12px' }} />;
    }
  };

  return (
    <div className="dashboard-panel activity-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div className="panel-title" style={{ marginBottom: 0 }}>
          <FontAwesomeIcon icon={faBell} /> Recent Activity
        </div>
        <button 
          style={{ color: '#3b82f6', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={onViewAllClick || (() => navigate(`/propertydashboard/${propertyId}/activity`))}
        >
          View All
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflow: 'hidden' }}>
        {recentActivity.map((activity, index) => (
          <div key={index} style={{ padding: '6px 0', backgroundColor: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flexShrink: 0 }}>
                {getStatusIcon(activity.status)}
              </div>
              <div style={{ flex: 1, minWidth: 0, backgroundColor: '#fff', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: '1.3', fontWeight: '500', backgroundColor: '#fff', textAlign: 'left' }}>{activity.message}</div>
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', backgroundColor: '#fff', textAlign: 'left' }}>{activity.timestamp}</div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  padding: '2px 6px', 
                  borderRadius: '4px', 
                  fontSize: '9px', 
                  backgroundColor: '#f1f5f9', 
                  color: '#64748b',
                  fontWeight: '500'
                }}>
                  {activity.type}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ElectricalSafetyDashboard = ({ onNavigate }) => {
  const { id: propertyId } = useParams();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  const attentionItems = stats.filter(item => item.failed > 0);
    
  return (
    <div className="electrical-dashboard-wrapper">
      <div className="dashboard-header-row">
        <h2 className="dashboard-title">Electrical Safety Dashboard</h2>
        <div className="dashboard-actions">
          <button className="btn btn-primary" onClick={() => { setComingSoonFeature('View Alerts'); setIsComingSoonOpen(true); }}>
            <FontAwesomeIcon icon={faBell} /> View Alerts
          </button>
          <button className="btn btn-primary" onClick={() => { setComingSoonFeature('Generate Report'); setIsComingSoonOpen(true); }}>
            <FontAwesomeIcon icon={faCalendar} /> Generate Report
          </button>
        </div>
      </div>
      <div className="dashboard-cards-grid">
        {stats.map((item) => (
          <div key={item.key} className="dashboard-card" onClick={() => onNavigate && item.navTarget && onNavigate(item.navTarget)} role={item.navTarget ? 'button' : undefined} tabIndex={item.navTarget ? 0 : undefined}>
            <div className="dashboard-card-icon">
              <FontAwesomeIcon icon={item.icon} />
            </div>
            <div className="dashboard-card-info">
              <div className="dashboard-card-label">{item.label}</div>
              <div className="dashboard-card-numbers">
                <span className="dashboard-card-total">{item.total} Assets</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="dashboard-bottom-panels">
        <div className="dashboard-panel attention-panel">
          <div className="panel-title">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Required Attention
          </div>
          {attentionItems.length > 0 ? (
            <ul className="attention-list">
              {attentionItems.map(item => (
                <li key={item.key}>
                  <FontAwesomeIcon icon={item.icon} className="attention-icon" />
                  <span className="attention-label">{item.label}</span>
                  <span className="attention-failed">{item.failed} Failed</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="no-issues">All systems are operational. No issues require attention.</div>
          )}
        </div>
        <SafetyRiskCard />
        <RecentActivityCard onViewAllClick={() => { setComingSoonFeature('Activity History'); setIsComingSoonOpen(true); }} />
      </div>
      <ComingSoonModal
        isOpen={isComingSoonOpen}
        onClose={() => setIsComingSoonOpen(false)}
        featureName={comingSoonFeature}
      />
    </div>
  );
};

export default ElectricalSafetyDashboard;